import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useCustomMutation, useList, useNotification } from '@refinedev/core'
import { UUID } from 'crypto'
import { useState } from 'react'
import {
  TestProfileResponse,
  TestProfileResult,
} from '../interfaces/scanInterfaces'

interface TestProfileProps {
  opportunityUuid: UUID | undefined
  draftProfileText: string
  funderUuid: UUID | undefined
}

export default function TestProfile({
  opportunityUuid,
  draftProfileText,
  funderUuid,
}: TestProfileProps) {
  const [numOfNeeds, setNumOfNeeds] = useState<number>(25)
  const [minExistingRank, setMinExistingRank] = useState<number>(3)
  const [selectedOrg, setSelectedOrg] = useState<any>(null)
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const [testResults, setTestResults] = useState<TestProfileResult[]>([])
  const [lastTestedProfile, setLastTestedProfile] = useState<string>('')

  const hasProfileChanged = draftProfileText !== lastTestedProfile

  const { open: openNotification } = useNotification()

  const { data: orgsData, isLoading: orgsLoading } = useList({
    resource: 'orgs',
    pagination: { pageSize: 1000 },
  })

  const { mutate: testProfile } = useCustomMutation<TestProfileResponse>()
  const { mutate: reTestProfile } = useCustomMutation<TestProfileResponse>()

  async function handleTestProfile(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    setIsScanning(true)

    testProfile(
      {
        url: `/opportunities/test-profile/${opportunityUuid}`,
        method: 'post',
        values: {
          min_rank: minExistingRank,
          number_of_needs: numOfNeeds,
          org_uuid: selectedOrg?.uuid || null,
          draft_profile_text: draftProfileText,
          funder_uuid: funderUuid,
        },
      },
      {
        onSuccess: async (response) => {
          openNotification?.({
            message: 'Test Profile completed successfully',
            type: 'success',
          })
          setIsScanning(false)
          setTestResults(response.data?.combined_results || [])
          setLastTestedProfile(draftProfileText)
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to run test profile',
            type: 'error',
          })
          setIsScanning(false)
        },
      }
    )
  }
  async function handleReTestProfile(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    setIsScanning(true)

    // retest profile with same settings against current test results

    reTestProfile(
      {
        url: `/opportunities/re-test-profile/${opportunityUuid}`,
        method: 'post',
        values: {
          current_results: testResults,
          draft_profile_text: draftProfileText,
          funder_uuid: funderUuid,
        },
      },
      {
        onSuccess: async (response) => {
          openNotification?.({
            message: 'Test Profile completed successfully',
            type: 'success',
          })
          setIsScanning(false)
          setTestResults(response.data?.combined_results || [])
          setLastTestedProfile(draftProfileText)
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to run test profile',
            type: 'error',
          })
          setIsScanning(false)
        },
      }
    )
  }

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 2,
        mt: 2,
        border: '1px solid #ccc',
        borderRadius: '4px',
        p: 2,
        backgroundColor: '#f5f5f5',
      }}
      autoComplete="off"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          width: '80%',
          mx: 'auto',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <TextField
          id="number of needs"
          label="Number of Needs to Test"
          variant="outlined"
          value={numOfNeeds}
          disabled={isScanning}
          type="number"
          onChange={(e) => setNumOfNeeds(parseInt(e.target.value))}
          sx={{ width: '200px', bgcolor: 'white' }}
        />
        <FormControl sx={{ bgcolor: 'white', width: '200px' }}>
          <InputLabel id="min-existing-rank-label">
            Minimum Existing Rank
          </InputLabel>
          <Select
            labelId="min-existing-rank-label"
            id="min-existing-rank"
            label="Minimum Existing Rank"
            variant="outlined"
            value={minExistingRank}
            disabled={isScanning}
            onChange={(e) => setMinExistingRank(e.target.value as number)}
          >
            {[...Array(5)].map((_, index) => (
              <MenuItem key={index + 1} value={index + 1}>
                {index + 1}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          options={orgsData?.data || []}
          getOptionLabel={(option) => `${option.name} `}
          value={selectedOrg}
          onChange={(_, newValue) => setSelectedOrg(newValue)}
          loading={orgsLoading}
          disabled={isScanning}
          sx={{ width: '500px', bgcolor: 'white' }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Organisation (Optional)"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {orgsLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
        {testResults.length === 0 ? (
          <Button
            type="submit"
            variant="contained"
            sx={{ width: '200px' }}
            disabled={isScanning}
            onClick={(e) => handleTestProfile(e)}
          >
            {isScanning ? 'Testing...' : 'Test'}
          </Button>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {!hasProfileChanged && (
              <Typography variant="caption" textAlign={'center'}>
                Make changes to the draft profile to re-test with the same set
              </Typography>
            )}
            {isScanning && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress sx={{ color: '#1C3045' }} size={18} />
                <Typography variant="caption">Testing...</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                sx={{ minWidth: '200px' }}
                disabled={isScanning || !hasProfileChanged}
                onClick={(e) => handleReTestProfile(e)}
              >
                Re-test with Same Set
              </Button>
              <Button
                variant="outlined"
                sx={{ minWidth: '200px' }}
                disabled={isScanning}
                onClick={(e) => handleTestProfile(e)}
              >
                Re-test with New Set
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {testResults.length === 0 && lastTestedProfile && !isScanning && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No previous scan results found and no orgs match the hard filters
            (amount, location, timing, etc.) for this opportunity.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
          >
            Please update and save the hard filters to try again.
          </Typography>
        </Box>
      )}

      {testResults.length > 0 &&
        (() => {
          const hasExistingScanResults = testResults.some(
            (r) => r.current_rank > 0 || r.current_result !== 'no_match'
          )

          return (
            <Box sx={{ mt: 3 }}>
              {!hasExistingScanResults && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid #1976d2',
                    borderRadius: '8px',
                    backgroundColor: '#e3f2fd',
                  }}
                >
                  <Typography variant="body2" color="primary">
                    <strong>Note:</strong> No existing scan results found for
                    this opportunity. Showing potential matches based on hard
                    filters (amount, location, timing, etc.).
                  </Typography>
                </Box>
              )}

              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Org</TableCell>
                      <TableCell>Need</TableCell>
                      {hasExistingScanResults && (
                        <>
                          <TableCell align="center">Current Rank</TableCell>
                          <TableCell align="center">New Rank</TableCell>
                          <TableCell>Current Result</TableCell>
                          <TableCell>New Result</TableCell>
                          <TableCell align="center">Change</TableCell>
                        </>
                      )}
                      {!hasExistingScanResults && (
                        <>
                          <TableCell align="center" sx={{ minWidth: '200px' }}>
                            Potential Rank
                          </TableCell>
                          <TableCell>Potential Result</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.map((result) => {
                      const rankChange = result.new_rank - result.current_rank

                      return (
                        <TableRow
                          key={`${result.org_uuid}-${result.need_id}`}
                          sx={{
                            backgroundColor: hasExistingScanResults
                              ? rankChange > 0
                                ? '#e8f5e9'
                                : rankChange < 0
                                ? '#ffebee'
                                : 'inherit'
                              : 'inherit',
                          }}
                        >
                          <TableCell>{result.org_name}</TableCell>
                          <TableCell>{result.need_title}</TableCell>
                          {hasExistingScanResults && (
                            <>
                              <TableCell align="center">
                                {result.current_rank}
                              </TableCell>
                              <TableCell align="center">
                                {result.new_rank}
                              </TableCell>
                              <TableCell>{result.current_result}</TableCell>
                              <TableCell>{result.new_result}</TableCell>
                              <TableCell align="center">
                                {rankChange > 0 ? (
                                  <Typography
                                    color="success.main"
                                    fontWeight="bold"
                                  >
                                    +{rankChange}
                                  </Typography>
                                ) : rankChange < 0 ? (
                                  <Typography
                                    color="error.main"
                                    fontWeight="bold"
                                  >
                                    {rankChange}
                                  </Typography>
                                ) : (
                                  <Typography color="text.secondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            </>
                          )}
                          {!hasExistingScanResults && (
                            <>
                              <TableCell align="center">
                                {result.new_rank}
                              </TableCell>
                              <TableCell>{result.new_result}</TableCell>
                            </>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )
        })()}
    </Box>
  )
}
